import { useEffect, useState, useRef } from "react";
import { chat as chatApi } from "../../lib/api";
import { useAuth } from "../../context/AuthContext";
import { useLang } from "../../context/LangContext";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Send, Loader2, MessageCircleOff } from "lucide-react";
import { toast } from "sonner";
import { Link } from "react-router-dom";

export default function ChatView() {
  const { user } = useAuth();
  const { t } = useLang();
  const [messages, setMessages] = useState([]);
  const [text, setText] = useState("");
  const [sending, setSending] = useState(false);
  const bottomRef = useRef(null);

  useEffect(() => {
    if (!user) return;
    const load = async () => {
      try {
        const data = await chatApi.list();
        setMessages(Array.isArray(data) ? data : data?.messages || []);
      } catch {
        /* keep silent */
      }
    };
    load();
    const id = setInterval(load, 4000);
    return () => clearInterval(id);
  }, [user]);

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages]);

  if (!user) {
    return (
      <div
        className="h-[calc(100vh-160px)] flex flex-col items-center justify-center px-8 text-center"
        data-testid="chat-locked"
      >
        <div className="h-16 w-16 rounded-2xl bg-[#1c1c1e] border border-white/8 flex items-center justify-center mb-5">
          <MessageCircleOff className="h-7 w-7 text-white/50" />
        </div>
        <h2 className="text-xl font-display font-semibold mb-2">
          {t("app.chat_locked_title")}
        </h2>
        <p className="text-white/55 text-sm mb-6 max-w-xs">
          {t("app.chat_locked_text")}
        </p>
        <Link to="/app/profile">
          <Button
            data-testid="chat-login-btn"
            className="bg-[#007AFF] hover:bg-[#0A84FF] text-white rounded-full px-6 h-11 font-semibold"
          >
            {t("app.chat_go_login")}
          </Button>
        </Link>
      </div>
    );
  }

  const send = async (e) => {
    e.preventDefault();
    if (!text.trim() || sending) return;
    setSending(true);
    try {
      const msg = await chatApi.send(text);
      setMessages((prev) => [...prev, msg]);
      setText("");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Не удалось отправить");
    } finally {
      setSending(false);
    }
  };

  const initials = (n) => (n || "U").slice(0, 2).toUpperCase();
  const myId = user.id || user._id || user.email;

  return (
    <div
      className="h-[calc(100vh-128px)] flex flex-col"
      data-testid="chat-view"
    >
      <div className="px-4 pt-6 pb-3">
        <h1 className="font-display text-3xl font-bold tracking-tight">
          {t("app.tab_chat")}
        </h1>
        <p className="text-white/50 text-sm">{t("app.chat_subtitle")}</p>
      </div>

      <div className="flex-1 overflow-y-auto px-4 py-2 space-y-3">
        {messages.length === 0 ? (
          <div className="h-full flex items-center justify-center text-white/40 text-sm py-20">
            Нет сообщений. Будьте первым!
          </div>
        ) : (
          messages.map((m) => {
            const mine =
              (m.user_id || m.userId || m.author) === myId ||
              (m.email && m.email === user.email);
            const name =
              m.username || m.author || m.user?.username || "Оператор";
            return (
              <div
                key={m.id || m._id}
                data-testid={`chat-message-${m.id || m._id}`}
                className={`flex items-end gap-2 ${mine ? "flex-row-reverse" : ""}`}
              >
                <div
                  className={`h-8 w-8 rounded-full text-xs font-semibold flex items-center justify-center shrink-0 ${mine ? "bg-[#FF6B35]/20 text-[#FF6B35]" : "bg-white/10 text-white"}`}
                >
                  {initials(name)}
                </div>
                <div
                  className={`max-w-[78%] rounded-2xl px-3.5 py-2 ${
                    mine
                      ? "bg-[#FF6B35] text-white rounded-br-md"
                      : "bg-[#1c1c1e] text-white rounded-bl-md border border-white/5"
                  }`}
                >
                  {!mine && (
                    <p className="text-[11px] text-[#007AFF] font-semibold mb-0.5">
                      {name}
                    </p>
                  )}
                  <p className="text-sm leading-relaxed break-words">
                    {m.text || m.message}
                  </p>
                  <p
                    className={`text-[10px] mt-1 ${mine ? "text-white/70" : "text-white/40"}`}
                  >
                    {m.created_at || m.timestamp
                      ? new Date(m.created_at || m.timestamp).toLocaleTimeString(
                          [],
                          { hour: "2-digit", minute: "2-digit" },
                        )
                      : ""}
                  </p>
                </div>
              </div>
            );
          })
        )}
        <div ref={bottomRef} />
      </div>

      <form
        onSubmit={send}
        data-testid="chat-form"
        className="ios-tabbar p-3 flex items-center gap-2"
      >
        <Input
          value={text}
          onChange={(e) => setText(e.target.value)}
          placeholder={t("app.chat_input_ph")}
          data-testid="chat-input"
          className="bg-[#1c1c1e] border-white/10 text-white h-11 rounded-full px-4 focus:border-[#007AFF]"
        />
        <Button
          type="submit"
          disabled={sending || !text.trim()}
          data-testid="chat-send-btn"
          className="bg-[#007AFF] hover:bg-[#0A84FF] h-11 w-11 p-0 rounded-full shrink-0"
        >
          {sending ? (
            <Loader2 className="h-4 w-4 animate-spin" />
          ) : (
            <Send className="h-4 w-4" />
          )}
        </Button>
      </form>
    </div>
  );
}
